import React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, GripVertical, Lock, Shapes, Trash2, Type } from 'lucide-react';
import clsx from 'clsx';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { ELEMENT_TYPES, getElementObjectUrl, getTextDisplayName } from '../../../../core/documentSchema';

import scss from './LayerItem.module.scss';

function LayerItem({ element, isSelected, onSelect, onToggleVisibility, onDelete }) {
  const { t } = useTranslation('mediaStudio');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: element.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isText = element.type === ELEMENT_TYPES.TEXT;
  const thumbUrl = getElementObjectUrl(element);
  const displayName = isText ? getTextDisplayName(element) : element.name;
  const visibilityLabel = element.visible ? t('hideLayer') : t('showLayer');

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(scss.layer, isSelected && scss.selected, isDragging && scss.dragging)}>
      <button
        type='button'
        className={scss.dragHandle}
        title={t('dragLayer')}
        {...attributes}
        {...listeners}
        aria-label={`${t('dragLayer')}: ${displayName}`}>
        <GripVertical size={14} />
      </button>

      <button
        type='button'
        className={scss.select}
        aria-pressed={isSelected}
        onClick={(event) => onSelect(element.id, event)}>
        <span className={scss.thumb}>
          {isText ? (
            <Type size={16} />
          ) : element.type === ELEMENT_TYPES.SHAPE ? (
            <Shapes size={16} />
          ) : thumbUrl ? (
            <img src={thumbUrl} alt='' />
          ) : null}
        </span>
        <span className={scss.name}>{displayName}</span>
      </button>

      <div className={scss.actions}>
        <button
          type='button'
          className={scss.iconBtn}
          title={visibilityLabel}
          aria-label={visibilityLabel}
          aria-pressed={!element.visible}
          onClick={() => onToggleVisibility(element.id)}>
          {element.visible ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
        {element.locked ? (
          <span className={scss.lockIcon} title={t('lockedLayer')} aria-label={t('lockedLayer')} role='img'>
            <Lock size={14} />
          </span>
        ) : (
          <button
            type='button'
            className={scss.iconBtn}
            title={t('removeLayer')}
            aria-label={t('removeLayer')}
            onClick={() => onDelete(element.id)}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

export default LayerItem;
